import { initFirebaseCore } from '@davids-beacon/safety-core'
import { initializeApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import { getAuth, GoogleAuthProvider, FacebookAuthProvider } from 'firebase/auth'
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore'
import { getDatabase } from 'firebase/database'

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean
  }
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
}

export const firebaseApp = initializeApp(firebaseConfig)

// AppCheck: debug token on localhost, reCAPTCHA v3 on prod
const isLocalhost =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

if (isLocalhost) {
  // Set to saved debug token, or `true` to auto-generate one (logged to console)
  window.FIREBASE_APPCHECK_DEBUG_TOKEN =
    (import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN as string | undefined) ?? true
}

// Skip AppCheck on Firebase Hosting preview-channel hosts (e.g. davids-beacon--staging-<hash>.web.app):
// the reCAPTCHA site key's allowed-domains list is prod-only, so AppCheck would throw
// (appCheck/recaptcha-error) and trip the verify-render gate. AppCheck is not enforced on the
// backend yet (enforcement deferred), so this has no functional impact on staging. Revisit when
// AppCheck enforcement goes live — then add the staging host to the reCAPTCHA allowed domains
// instead, and remove this guard.
const isPreviewChannelHost =
  typeof window !== 'undefined' && window.location.hostname.startsWith('davids-beacon--')

if (import.meta.env.VITE_RECAPTCHA_SITE_KEY && !isPreviewChannelHost) {
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaV3Provider(import.meta.env.VITE_RECAPTCHA_SITE_KEY as string),
    isTokenAutoRefreshEnabled: true,
  })
}

export const auth = getAuth(firebaseApp)
export const googleProvider = new GoogleAuthProvider()
export const facebookProvider = new FacebookAuthProvider()
facebookProvider.addScope('email')
facebookProvider.addScope('public_profile')

export const db = initializeFirestore(firebaseApp, {
  ignoreUndefinedProperties: true,
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
})
export const rtdb = getDatabase(firebaseApp)

// Storage — lazy, only needed on photo upload
let _storage: Awaited<ReturnType<typeof import('firebase/storage')['getStorage']>> | null = null
export async function getStorageInstance() {
  if (!_storage) {
    const { getStorage } = await import('firebase/storage')
    _storage = getStorage(firebaseApp)
  }
  return _storage
}

// Cloud Functions — lazy
let _functions: Awaited<ReturnType<typeof import('firebase/functions')['getFunctions']>> | null = null
export async function getFunctionsInstance() {
  if (!_functions) {
    const { getFunctions } = await import('firebase/functions')
    _functions = getFunctions(firebaseApp, 'asia-southeast1')
  }
  return _functions
}

// FCM — lazy, web only
let _messaging: ReturnType<typeof import('firebase/messaging')['getMessaging']> | null = null
export async function getMessagingInstance() {
  if (_messaging) return _messaging
  const { getMessaging, isSupported } = await import('firebase/messaging')
  if (!(await isSupported())) return null
  _messaging = getMessaging(firebaseApp)
  return _messaging
}

initFirebaseCore({
  db,
  auth,
  rtdb,
  googleProvider,
  facebookProvider,
  getStorageInstance,
  getFunctionsInstance,
  getMessagingInstance,
})
